import api from '../utils/api';

// Generate free form content
export const generateFreeFormContent = async (formData) => {
  try {
    // Generate unique agent_id and session_id (following the same pattern as other endpoints)
    const timestamp = Date.now();
    const agentId = `agent_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    const sessionId = `session_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Map formData to the expected API payload structure
    const requestPayload = {
      request_type: "free_form",
      additional_data: {
        agent_id: agentId,
        session_id: sessionId,
        streaming: false,
        further_details: formData.prompt || "",
        language: formData.language || "",
        location_focus: formData.locationFocus || "",
        role: formData.role || "",
        goal: formData.goal || "",
        constraints: formData.constraints || ""
      }
    };
    
    const response = await api.post('/generate/free_form', requestPayload);
    return response.data;
  } catch (error) {
    console.error('Error generating free form content:', error);
    throw error;
  }
};

// Save free form content as draft
export const saveFreeFormContentDraft = async (contentData) => {
  try {
    
    const requestPayload = {
      free_form_content: contentData
    };
    const response = await api.post('/api/v1/free_form_contents/save_draft', requestPayload);
    return response.data;
  } catch (error) {
    console.error('Error saving draft:', error);
    console.error('Error response:', error.response);
    if (error.response) {
      console.error('Error response data:', error.response.data);
      console.error('Error response status:', error.response.status);
    }
    throw error;
  }
};

// Get all free form content
export const getFreeFormContent = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.per_page) queryParams.append('per_page', filters.per_page);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.language) queryParams.append('language', filters.language);
    if (filters.location_focus) queryParams.append('location_focus', filters.location_focus);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.source) queryParams.append('source', filters.source);
    
    const response = await api.get(`/api/v1/free_form_contents?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching free form content:', error);
    throw error;
  }
};

// Get free form content by ID
export const getFreeFormContentById = async (id) => {
  try {
    const response = await api.get(`/api/v1/free_form_contents/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching free form content by ID:', error);
    throw error;
  }
};

// Update free form content
export const updateFreeFormContent = async (id, contentData) => {
  try {
    const response = await api.put(`/api/v1/free_form_contents/${id}`, {
      free_form_content: contentData
    });
    return response.data;
  } catch (error) {
    console.error('Error updating free form content:', error);
    throw error;
  }
};

// Update free form content draft using PATCH
export const updateFreeFormContentDraft = async (id, contentData) => {
  try {
    const response = await api.patch(`/api/v1/free_form_contents/${id}`, contentData);
    return response.data;
  } catch (error) {
    console.error('Error updating free form content draft:', error);
    throw error;
  }
};

// Delete free form content
export const deleteFreeFormContent = async (id) => {
  try {
    const response = await api.delete(`/api/v1/free_form_contents/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting free form content:', error);
    throw error;
  }
};

// Translate free form content
export const translateFreeFormContent = async (id, targetLanguage) => {
  try {
    const response = await api.post(`/api/v1/free_form_contents/${id}/translate`, {
      targetLanguage
    });
    return response.data;
  } catch (error) {
    console.error('Error translating free form content:', error);
    throw error;
  }
};

// Get Shopify pages data
export const getShopifyPages = async () => {
  try {
    const response = await api.get('/api/v1/free_form_contents/shopify_pages');
    return response.data;
  } catch (error) {
    console.error('Error fetching Shopify pages:', error);
    throw error;
  }
};

// Get Shopify page by resource ID
export const getShopifyPage = async (resourceId, resourceType) => {
  try {
    const response = await api.get(`/api/v1/free_form_contents/get_page`, {
      params: {
        resource_id: resourceId,
        resource_type: resourceType
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching Shopify page:', error);
    throw error;
  }
};

// Mock data for development/testing
export const mockFreeFormContent = [
  {
    id: 1,
    name: "Organic Ashwagandha Tablets - Health Benefits",
    prompt: "Write about the health benefits of organic ashwagandha tablets",
    created: "Aug 7, 2025",
    language: "English",
    dataStructure: "object",
    renderingFormat: "bullet points",
    locationFocus: "India",
    status: "published",
    image: "https://images.unsplash.com/photo-1584308666744-24d5b474e2b5?w=400&h=400&fit=crop"
  },
  {
    id: 2,
    name: "Pure Himalayan Pink Salt - Dr. Recommendation",
    prompt: "Create content about Himalayan pink salt recommendations from doctors",
    created: "Aug 6, 2025",
    language: "English",
    dataStructure: "array",
    renderingFormat: "table",
    locationFocus: "India",
    status: "draft",
    image: ""
  },
  {
    id: 3,
    name: "Ethical Coffee Beans - Sustainable Sourcing",
    prompt: "Explain sustainable sourcing practices for ethical coffee beans",
    created: "Aug 5, 2025",
    language: "English",
    dataStructure: "string",
    renderingFormat: "paragraph",
    locationFocus: "USA",
    status: "published",
    image: "https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400&h=400&fit=crop"
  },
  {
    id: 4,
    name: "Handcrafted Leather Wallet - Premium Quality",
    prompt: "Describe the premium quality features of handcrafted leather wallets",
    created: "Aug 4, 2025",
    language: "English",
    dataStructure: "object",
    renderingFormat: "bullet points",
    locationFocus: "UK",
    status: "published",
    image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=400&fit=crop"
  },
  {
    id: 5,
    name: "Smart Home Security Camera - Features & Setup",
    prompt: "List features and setup instructions for smart home security cameras",
    created: "Aug 3, 2025",
    language: "English",
    dataStructure: "array",
    renderingFormat: "table",
    locationFocus: "Canada",
    status: "draft",
    image: ""
  },
  {
    id: 6,
    name: "Eco-Friendly Cleaning Spray - Natural Ingredients",
    prompt: "Write about natural ingredients in eco-friendly cleaning sprays",
    created: "Aug 2, 2025",
    language: "English",
    dataStructure: "string",
    renderingFormat: "paragraph",
    locationFocus: "USA",
    status: "published",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=400&fit=crop"
  }
];

// Generate content with streaming from 4ay API
export const generateContentWithStreaming = async (formData, onChunk, onComplete, onError) => {
  try {
    
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      if (onError) {
        onError(error);
      }
      throw error;
    }
    
    // Generate unique agent_id and session_id (following the same pattern as other endpoints)
    const timestamp = Date.now();
    const agentId = `agent_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    const sessionId = `session_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    // Map formData to the expected API payload structure
    const requestPayload = {
      request_type: "free_form",
      additional_data: {
        agent_id: agentId,
        session_id: sessionId,
        streaming: true,
        further_details: formData.prompt || "",
        language: formData.language || "",
        location_focus: formData.locationFocus || "",
        role: formData.role || "",
        goal: formData.goal || "",
        constraints: formData.constraints || "",
        image: formData.image || "", // Include image in the payload
      }
    };
    
    // Use fetch API for proper SSE handling
    const response = await fetch(`${api.defaults.baseURL}/generate/free_form`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      if (response.status === 401) {
        const error = new Error('Authentication failed. Please log in again.');
        // Clear the expired token
        localStorage.removeItem('token');
        // Redirect to login page only if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = "/login";
        }
        if (onError) {
          onError(error);
        }
        throw error;
      } else if (response.status === 403) {
        const error = new Error('Access forbidden. You may not have permission to perform this action.');
        if (onError) {
          onError(error);
        }
        throw error;
      } else if (response.status === 500) {
        const error = new Error('Server error. Please try again later.');
        if (onError) {
          onError(error);
        }
        throw error;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedData = '';
    let lastProcessedIndex = 0;
    let isProcessingChunk = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefi
            if (data.trim() === '') continue; // Skip empty lines
            accumulatedData += data;
          }
        }

        if (!isProcessingChunk && accumulatedData.length > lastProcessedIndex) {
          isProcessingChunk = true;
          
          try {
            const newData = accumulatedData.substring(lastProcessedIndex);
            const processedData = processStreamingData(newData, (chunk, htmlContent) => {
              if (onChunk) {
                onChunk(chunk, htmlContent);
              }
            });
            
            if (processedData.processedLength > 0) {
              lastProcessedIndex += processedData.processedLength;
            }
          } catch (error) {
            console.warn('Error processing streaming data:', error);
          } finally {
            isProcessingChunk = false;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Process any remaining data
    let finalParsedContent = '';
    try {
      const remainingData = accumulatedData.substring(lastProcessedIndex);
      
      if (remainingData.trim()) {
        const processedData = processStreamingData(remainingData, (chunk, htmlContent) => {
          if (onChunk) {
            onChunk(chunk, htmlContent);
          }
        });
        if (processedData.htmlContent) {
          finalParsedContent = processedData.htmlContent;
        }
      }
      
      // If we still don't have content, try to extract from the entire accumulated data
      if (!finalParsedContent) {
        finalParsedContent = extractFinalContent(accumulatedData);
      }
    } catch (error) {
      console.warn('Error processing final data:', error);
      // Fallback to accumulated content
      finalParsedContent = accumulatedData;
    }

    // Call onComplete with the final result
    const finalResult = {
      id: Date.now(),
      name: formData.prompt.substring(0, 50) + "...",
      prompt: formData.prompt,
      created: new Date().toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      language: formData.language,
      locationFocus: formData.locationFocus,
      generatedContent: finalParsedContent,
      status: "draft"
    };

    if (onComplete) {
      onComplete(finalResult);
    }

    return finalResult;

  } catch (error) {
    console.error('Error generating content with streaming:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

// Helper function to process streaming data and extract JSON objects
function processStreamingData(data, onChunk) {
  let processedLength = 0;
  let htmlContent = '';
  
  try {
    // First, try to find complete JSON objects
    const jsonObjects = extractCompleteJsonObjects(data);
    
    for (const jsonObj of jsonObjects) {
      try {
        const parsed = JSON.parse(jsonObj.json);
        
        // Extract HTML content
        if (parsed.html_content) {
          htmlContent = parsed.html_content;
        } else if (parsed.content) {
          htmlContent = parsed.content;
        }
        
        if (htmlContent && onChunk) {
          onChunk(jsonObj.json, htmlContent);
        }
        
        processedLength = jsonObj.endIndex + 1;
      } catch (parseError) {
        console.warn('Failed to parse JSON object:', parseError);
        
        // Try to fix malformed JSON
        try {
          const fixedJson = fixMalformedJson(jsonObj.json);
          const parsed = JSON.parse(fixedJson);
          
          if (parsed.html_content) {
            htmlContent = parsed.html_content;
          } else if (parsed.content) {
            htmlContent = parsed.content;
          }
          
          if (htmlContent && onChunk) {
            onChunk(fixedJson, htmlContent);
          }
          
          processedLength = jsonObj.endIndex + 1;
        } catch (fixError) {
          console.warn('Failed to fix malformed JSON:', fixError);
          // Continue to next JSON object
        }
      }
    }
    
    // If no valid JSON objects found, try to extract content directly
    if (!htmlContent) {
      const directContent = extractHtmlContentDirectly(data);
      if (directContent) {
        htmlContent = directContent;
        if (onChunk) {
          onChunk(data, directContent);
        }
        processedLength = data.length;
      }
    }
    
    // NEW: If still no content found, try to handle unquoted JSON response
    if (!htmlContent) {
      const unquotedContent = handleUnquotedJsonResponse(data);
      if (unquotedContent) {
        htmlContent = unquotedContent;
        if (onChunk) {
          onChunk(data, unquotedContent);
        }
        processedLength = data.length;
      }
    }
  } catch (error) {
    console.warn('Error in processStreamingData:', error);
    
    // Last resort: try to extract content directly
    try {
      const directContent = extractHtmlContentDirectly(data);
      if (directContent) {
        htmlContent = directContent;
        if (onChunk) {
          onChunk(data, directContent);
        }
        processedLength = data.length;
      }
    } catch (directError) {
      console.warn('Failed to extract content directly:', directError);
      
      // NEW: Try unquoted JSON handler as final fallback
      try {
        const unquotedContent = handleUnquotedJsonResponse(data);
        if (unquotedContent) {
          htmlContent = unquotedContent;
          if (onChunk) {
            onChunk(data, unquotedContent);
          }
          processedLength = data.length;
        }
      } catch (unquotedError) {
        console.warn('Failed to handle unquoted JSON response:', unquotedError);
      }
    }
  }
  
  return { processedLength, htmlContent };
}

// Helper function to extract complete JSON objects from fragmented data
function extractCompleteJsonObjects(data) {
  const jsonObjects = [];
  let braceCount = 0;
  let startIndex = -1;
  let currentIndex = 0;
  
  while (currentIndex < data.length) {
    const char = data[currentIndex];
    
    if (char === '{') {
      if (braceCount === 0) {
        startIndex = currentIndex;
      }
      braceCount++;
    } else if (char === '}') {
      braceCount--;
      if (braceCount === 0 && startIndex !== -1) {
        // Found a complete JSON object
        const jsonString = data.substring(startIndex, currentIndex + 1);
        
        // Validate that this looks like JSON
        if (isValidJsonStructure(jsonString)) {
          jsonObjects.push({
            json: jsonString,
            startIndex: startIndex,
            endIndex: currentIndex
          });
        }
        
        startIndex = -1;
      }
    }
    
    currentIndex++;
  }
  
  return jsonObjects;
}

// Helper function to validate JSON structure
function isValidJsonStructure(jsonString) {
  // Basic validation - check if it has common JSON patterns
  const trimmed = jsonString.trim();
  
  // Must start with { and end with }
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return false;
  }
  
  // Must have at least one key-value pair
  if (trimmed.length < 3) {
    return false;
  }
  
  // Check for common JSON patterns
  const hasQuotes = /"[^"]*":/.test(trimmed);
  const hasColon = trimmed.includes(':');
  
  return hasQuotes && hasColon;
}

// Helper function to extract final content from accumulated data
function extractFinalContent(accumulatedData) {
  try {
    // Try to find the last complete JSON object
    const jsonObjects = extractCompleteJsonObjects(accumulatedData);
    
    if (jsonObjects.length > 0) {
      const lastJson = jsonObjects[jsonObjects.length - 1];
      try {
        const parsed = JSON.parse(lastJson.json);
        
        if (parsed.html_content) {
          return parsed.html_content;
        } else if (parsed.content) {
          return parsed.content;
        }
      } catch (parseError) {
        console.warn('Failed to parse JSON in extractFinalContent:', parseError);
        // Try to clean up the JSON before parsing
        const cleaned = cleanupFragmentedData(lastJson.json);
        if (cleaned.html_content) {
          return cleaned.html_content;
        }
      }
    }
    
    // If no JSON found, try to clean up the accumulated data
    return cleanupFragmentedData(accumulatedData);
  } catch (error) {
    console.warn('Error extracting final content:', error);
    return cleanupFragmentedData(accumulatedData);
  }
}

// Helper function to clean up heavily fragmented data
// function cleanupFragmentedData(data) {
//   try {
//     // Only remove "data: " prefixes, preserve newlines and spaces
//     let cleaned = data.replace(/data:\s*/g, '');
    
//     // Try to find JSON-like content
//     const jsonMatch = cleaned.match(/\{.*\}/);
//     if (jsonMatch) {
//       const jsonString = jsonMatch[0];
      
//       // Try to fix common malformed JSON issues before parsing
//       const fixedJsonString = fixMalformedJson(jsonString);
      
//       try {
//         const parsed = JSON.parse(fixedJsonString);
        
//         if (parsed.html_content) {
//           return parsed.html_content;
//         } else if (parsed.content) {
//           return parsed.content;
//         }
//       } catch (parseError) {
//         console.warn('Failed to parse even after fixing JSON:', parseError);
//         // Try to extract HTML content directly from the fixed string
//         const htmlMatch = fixedJsonString.match(/"html_content"\s*:\s*"([^"]+)"/);
//         if (htmlMatch) {
//           return htmlMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
//         }
//       }
//     }
    
//     // If still no success, try to extract HTML content directly
//     const htmlContent = extractHtmlContentDirectly(cleaned);
//     if (htmlContent) {
//       return htmlContent;
//     }
    
//     // NEW: Try to handle unquoted JSON response
//     const unquotedContent = handleUnquotedJsonResponse(cleaned);
//     if (unquotedContent) {
//       return unquotedContent;
//     }
    
//     // Last resort: return the cleaned data
//     return cleaned;
//   } catch (error) {
//     console.warn('Error cleaning fragmented data:', error);
//     return data;
//   }
// }
function cleanupFragmentedData(data) {
  try {
    // Remove any "data: " prefixes
    let cleaned = data.replace(/data:\s*/g, '');

    // Extract HTML content first
    let htmlContent = '';
    const htmlMatch = cleaned.match(/"html_content"\s*:\s*"([\s\S]*?)"\s*(,|\})/);
    if (htmlMatch) {
      htmlContent = htmlMatch[1];
      // Remove HTML before parsing
      cleaned = cleaned.replace(htmlMatch[0], `"html_content": ""${htmlMatch[2]}`);
    }

    // Fix other common JSON issues
    cleaned = cleaned
      .replace(/""/g, '"')
      .replace(/meta\{/g, '"metadata":{')
      .replace(/False/g, 'false')
      .replace(/True/g, 'true')
      .replace(/,\s*}/g, '}')       // remove trailing commas before }
      .replace(/,\s*]/g, ']')       // remove trailing commas before ]
      // Fix missing or empty values for critical keys
      .replace(/"role_adaptation"\s*:\s*"/g, '"role_adaptation":"unknown"')
      .replace(/"constraints_considered"\s*:\s*}/g, '"constraints_considered":{}')
      .replace(/"constraints_considered":\s*}/g, '"constraints_considered":{}')
      .replace(/"constraints_considered":\s*,\s*}/g, '"constraints_considered":{}')
      // Fix empty values that cause JSON errors
      .replace(/:\s*,\s*}/g, ': null}')
      .replace(/:\s*,\s*]/g, ': null]')
      // Fix specific issues we're seeing
      .replace(/"constraints_considered":\s*}/g, '"constraints_considered":{}')
      .replace(/"constraints_considered":\s*,\s*}/g, '"constraints_considered":{}')
      .replace(/"constraints_considered":\s*,\s*"html_content"/g, '"constraints_considered":{},"html_content"')
      // Fix unterminated strings
      .replace(/"html_content":\s*"$/g, '"html_content":""')
      .replace(/"html_content":\s*"([^"]*?)\s*$/g, '"html_content":"$1"');

    // Parse JSON safely
    let parsed = JSON.parse(cleaned);

    // Attach HTML back
    parsed.html_content = htmlContent.replace(/\\"/g, '"').replace(/\\n/g, '\n');

    // Return the parsed object
    return parsed;

  } catch (error) {
    console.warn('Error cleaning fragmented data:', error);
    // Try to extract just the HTML content as fallback
    const htmlMatch = data.match(/"html_content"\s*:\s*"([\s\S]*?)"\s*(,|\})/);
    if (htmlMatch) {
      const htmlContent = htmlMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
      return { html_content: htmlContent };
    }
    return { html_content: data }; // fallback: return original data as HTML
  }
}


// Helper function to fix common malformed JSON issues
function fixMalformedJson(jsonString) {
  let fixed = jsonString;
  
  // First, check if this is already valid JSON
  try {
    JSON.parse(fixed);
    return fixed; // Already valid, return as-is
  } catch (e) {
    // Not valid JSON, proceed with fixing
  }
  
  // Handle completely unquoted JSON (like the failing response)
  // Pattern: key: value instead of "key": "value"
  
  // Fix missing quotes around keys (more comprehensive)
  fixed = fixed.replace(/(\w+(?:_\w+)*):/g, '"$1":');
  
  // Fix missing quotes around string values, but preserve numbers, booleans, null, and objects
  // This regex is more sophisticated to avoid over-quoting
  fixed = fixed.replace(/:\s*([^",{}[\]\s][^,{}[\]]*[^",{}[\]\s])\s*([,}])/g, (match, value, endChar) => {
    // Don't quote numbers, booleans, null, or objects
    if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
      return `: ${value}${endChar}`;
    }
    // Quote string values
    return `: "${value}"${endChar}`;
  });
  
  // Fix missing quotes around simple string values at the end of objects
  fixed = fixed.replace(/:\s*([^",{}[\]\s]+)\s*([,}])/g, (match, value, endChar) => {
    // Don't quote numbers, booleans, null, or objects
    if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
      return `: ${value}${endChar}`;
    }
    // Quote string values
    return `: "${value}"${endChar}`;
  });
  
  // Fix missing quotes around values that end with quotes
  fixed = fixed.replace(/:\s*([^",{}[\]\s][^"]*)"\s*([,}])/g, ': "$1"$2');
  
  // Fix missing quotes around values that start with quotes
  fixed = fixed.replace(/:\s*"\s*([^"]+)\s*([,}])/g, ': "$1"$2');
  
  // Handle edge case where the last value might not have a comma
  fixed = fixed.replace(/:\s*([^",{}[\]\s][^,{}[\]]*[^",{}[\]\s])\s*}$/g, (match, value) => {
    if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
      return `: ${value}}`;
    }
    return `: "${value}"}`;
  });
  
  return fixed;
}

// Specialized function to fix translation JSON content
function fixTranslationJsonContent(jsonString) {
  try {
    // First, try to parse as-is
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Initial JSON parse failed, attempting to fix translation JSON...');
    console.log('Original JSON string:', jsonString.substring(0, 200) + '...');
    
    let fixed = jsonString;
    
    // Fix Python-style values first (most common issue)
    // Be more aggressive about replacing None, True, False
    fixed = fixed
      .replace(/:\s*None\b/g, ': null')  // Replace Python None with JSON null
      .replace(/:\s*True\b/g, ': true')  // Replace Python True with JSON true
      .replace(/:\s*False\b/g, ': false') // Replace Python False with JSON false
      .replace(/\bNone\b/g, 'null')  // Replace standalone None with null
      .replace(/\bTrue\b/g, 'true')  // Replace standalone True with true
      .replace(/\bFalse\b/g, 'false') // Replace standalone False with false
      // Additional patterns to catch edge cases
      .replace(/"description":\s*None/g, '"description": null')
      .replace(/"description":None/g, '"description": null')
      .replace(/description":\s*None/g, 'description": null')
      .replace(/description":None/g, 'description": null');
    
    // Fix double quotes issues
    fixed = fixed.replace(/""/g, '"');
    
    // Remove trailing commas
    fixed = fixed.replace(/,\s*}/g, '}');
    fixed = fixed.replace(/,\s*]/g, ']');
    
    console.log('After basic fixes:', fixed.substring(0, 200) + '...');
    
    // Additional specific fixes for common patterns
    fixed = fixed
      // Fix any remaining None values that might have been missed
      .replace(/"([^"]+)":\s*None/g, '"$1": null')
      .replace(/"([^"]+)":None/g, '"$1": null')
      // Fix any remaining True/False values
      .replace(/"([^"]+)":\s*True/g, '"$1": true')
      .replace(/"([^"]+)":\s*False/g, '"$1": false')
      .replace(/"([^"]+)":True/g, '"$1": true')
      .replace(/"([^"]+)":False/g, '"$1": false');
    
    console.log('After specific pattern fixes:', fixed.substring(0, 200) + '...');
    
    try {
      return JSON.parse(fixed);
    } catch (secondError) {
      console.warn('Basic fixes failed, trying more targeted fixes...');
      console.log('Error:', secondError.message);
      
      // More targeted fixes for specific patterns
      fixed = fixed
        // Fix unquoted keys (be more careful about this)
        .replace(/(\w+(?:_\w+)*):/g, (match, key) => {
          // Only add quotes if the key is not already quoted
          if (!match.includes('"')) {
            return `"${key}":`;
          }
          return match;
        })
        // Fix unquoted string values (be more careful)
        .replace(/:\s*([^",{}[\]\s][^,{}[\]]*[^",{}[\]\s])\s*([,}])/g, (match, value, endChar) => {
          // Don't quote numbers, booleans, null, or objects
          if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
            return `: ${value}${endChar}`;
          }
          // Only quote if it's not already quoted
          if (!value.startsWith('"') && !value.endsWith('"')) {
            return `: "${value}"${endChar}`;
          }
          return match;
        })
        // Fix unquoted values at the end of objects
        .replace(/:\s*([^",{}[\]\s][^,{}[\]]*[^",{}[\]\s])\s*}$/g, (match, value) => {
          if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
            return `: ${value}}`;
          }
          if (!value.startsWith('"') && !value.endsWith('"')) {
            return `: "${value}"}`;
          }
          return match;
        });
      
      console.log('After targeted fixes:', fixed.substring(0, 200) + '...');
      
      try {
        return JSON.parse(fixed);
      } catch (thirdError) {
        console.error('All JSON parsing attempts failed:', thirdError);
        console.error('Final fixed string:', fixed.substring(0, 500) + '...');
        console.error('Error at position:', thirdError.message);
        
        // Try to find the exact error position
        try {
          const testParse = JSON.parse(fixed);
          return testParse;
        } catch (finalError) {
          console.error('Final parse error:', finalError.message);
          return null;
        }
      }
    }
  }
}

function extractHtmlContentDirectly(data) {
  try {
    let htmlMatch = data.match(/"html_content"\s*:\s*"([^"]+)"/);
    if (!htmlMatch) {
      htmlMatch = data.match(/html_content\s*:\s*"([^"]+)"/);
    }
    if (!htmlMatch) {
      htmlMatch = data.match(/html\s*_content\s*:\s*"([^"]+)"/);
    }
    if (!htmlMatch) {
      const htmlContentIndex = data.indexOf('html_content');
      if (htmlContentIndex !== -1) {
        const afterHtmlContent = data.substring(htmlContentIndex);
        const htmlValueMatch = afterHtmlContent.match(/:\s*"([^"]*<[^"]*>[^"]*)/);
        if (htmlValueMatch) {
          const content = htmlValueMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
          return content;
        }
      }
    }
    
    // NEW: Handle completely unquoted malformed JSON (like the failing response)
    if (!htmlMatch) {
      // Look for pattern: html_content: "content" (unquoted key, quoted value)
      htmlMatch = data.match(/html_content\s*:\s*"([^"]+)"/);
    }
    if (!htmlMatch) {
      // Look for pattern: html_content: content (unquoted key and value)
      const htmlContentIndex = data.indexOf('html_content');
      if (htmlContentIndex !== -1) {
        const afterHtmlContent = data.substring(htmlContentIndex);
        // Look for content after the colon, handling both quoted and unquoted values
        const htmlValueMatch = afterHtmlContent.match(/:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
        if (htmlValueMatch) {
          const content = htmlValueMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
          return content;
        }
      }
    }
    
    if (htmlMatch) {
      const content = htmlMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      return content;
    }
    
    // Look for content field (also handle unquoted keys)
    let contentMatch = data.match(/"content"\s*:\s*"([^"]+)"/);
    if (!contentMatch) {
      // Try without quotes around the key (malformed JSON)
      contentMatch = data.match(/content\s*:\s*"([^"]+)"/);
    }
    if (!contentMatch) {
      // Try unquoted key and value
      const contentIndex = data.indexOf('content');
      if (contentIndex !== -1) {
        const afterContent = data.substring(contentIndex);
        const contentValueMatch = afterContent.match(/:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
        if (contentValueMatch) {
          const content = contentValueMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
          return content;
        }
      }
    }
    
    if (contentMatch) {
      const content = contentMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      return content;
    }
    
    // Look for HTML-like content between quotes that contains HTML tags
    const htmlLikeMatch = data.match(/"[^"]*<[^"]*>[^"]*"/);
    if (htmlLikeMatch) {
      const content = htmlLikeMatch[0].replace(/^"/, '').replace(/"$/, '');
      return content;
    }
    
    // Look for any content that looks like HTML (broader search)
    const broadHtmlMatch = data.match(/<[^>]+>[^<]*<\/[^>]+>/);
    if (broadHtmlMatch) {
      return broadHtmlMatch[0];
    }
    
    // NEW: Try to extract content from the malformed JSON structure
    // Look for any text that appears to be HTML content in the response
    const potentialHtmlMatch = data.match(/(?:html_content|content)\s*:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
    if (potentialHtmlMatch) {
      const content = potentialHtmlMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      return content;
    }
    
    return null;
  } catch (error) {
    console.warn('Error extracting HTML content directly:', error);
    return null;
  }
}

function handleUnquotedJsonResponse(data) {
  try {
    const htmlContentMatch = data.match(/(?:html_content|content)\s*:\s*("?)([^"]*<[^"]*>[^"]*)\1/);
    if (htmlContentMatch) {
      const content = htmlContentMatch[2].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      return content;
    }
    
    let reconstructedJson = data;
    
    reconstructedJson = reconstructedJson.replace(/(\w+(?:_\w+)*):/g, '"$1":');
    reconstructedJson = reconstructedJson.replace(/:\s*([^",{}[\]\s][^,{}[\]]*[^",{}[\]\s])\s*([,}])/g, (match, value, endChar) => {
      // Don't quote numbers, booleans, null, or objects
      if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
        return `: ${value}${endChar}`;
      }
      return `: "${value}"${endChar}`;
    });
    
    reconstructedJson = reconstructedJson.replace(/:\s*([^",{}[\]\s][^,{}[\]]*[^",{}[\]\s])\s*}$/g, (match, value) => {
      if (/^(true|false|null|\d+(?:\.\d+)?)$/.test(value) || value.startsWith('{') || value.startsWith('[')) {
        return `: ${value}}`;
      }
      return `: "${value}"}`;
    });
    
    if (!reconstructedJson.trim().startsWith('{')) {
      reconstructedJson = '{' + reconstructedJson + '}';
    }
    try {
      const parsed = JSON.parse(reconstructedJson);
      
      if (parsed.html_content) {
        return parsed.html_content;
      } else if (parsed.content) {
        return parsed.content;
      }
      
      return reconstructedJson;
    } catch (parseError) {
      console.warn('Failed to parse reconstructed JSON:', parseError);
      return reconstructedJson;
    }
  } catch (error) {
    console.warn('Error handling unquoted JSON response:', error);
    return null;
  }
}

// Translate content with streaming
export const translateContentWithStreaming = async (formData, onChunk, onComplete, onError) => {
  try {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      if (onError) {
        onError(error);
      }
      throw error;
    }

    // Use fetch API for proper SSE handling
    const response = await fetch(`${api.defaults.baseURL}/generates/translate_content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        request_type: "language_translate",
        additional_data: {
          json_data: {
            content: formData.content,
            metafields: formData.metafields || []
          },
          source_language: formData.source_language || "English",
          target_language: formData.target_language || "English",
          target_region: formData.target_region || "Global"
        }
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        const error = new Error('Authentication failed. Please log in again.');
        localStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
          window.location.href = "/login";
        }
        if (onError) {
          onError(error);
        }
        throw error;
      } else if (response.status === 403) {
        const error = new Error('Access forbidden. You may not have permission to perform this action.');
        if (onError) {
          onError(error);
        }
        throw error;
      } else if (response.status === 500) {
        const error = new Error('Server error. Please try again later.');
        if (onError) {
          onError(error);
        }
        throw error;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedData = '';
    let lastProcessedIndex = 0;
    let isProcessingChunk = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefix
            
            if (data.trim() === '') continue; // Skip empty lines
            
            // Accumulate all data
            accumulatedData += data;
          }
        }

        // Try to process accumulated data for complete JSON objects
        if (!isProcessingChunk && accumulatedData.length > lastProcessedIndex) {
          isProcessingChunk = true;
          
          try {
            // Look for complete JSON objects in the new accumulated data
            const newData = accumulatedData.substring(lastProcessedIndex);
            console.log('Processing translation chunk:', newData.substring(0, 200) + '...');
            
            const processedData = processStreamingData(newData, (chunk, htmlContent) => {
              console.log('Translation callback triggered with HTML content:', htmlContent ? htmlContent.substring(0, 100) + '...' : 'no content');
              
              // Clean the content before passing it to the callback
              let cleanedContent = htmlContent;
              if (cleanedContent && typeof cleanedContent === 'string') {
                // Remove JSON artifacts
                if (cleanedContent.includes('"content":')) {
                  const contentMatch = cleanedContent.match(/"content":\s*"([\s\S]*?)"\s*(,|\})/);
                  if (contentMatch) {
                    cleanedContent = contentMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
                  }
                }
                
                // Remove metafields JSON artifacts
                cleanedContent = cleanedContent
                  .replace(/",\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact
                  .replace(/,\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact (without quotes)
                  .replace(/",\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere
                  .replace(/,\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere (without quotes)
                  .replace(/\s+$/, '') // Remove trailing whitespace
                  .trim();
                
                // Convert plain text to HTML if needed
                if (cleanedContent && !/<[^>]+>/.test(cleanedContent)) {
                  cleanedContent = cleanedContent.split('\n\n').map(paragraph => 
                    paragraph.trim() ? `<p>${paragraph.trim()}</p>` : ''
                  ).join('');
                }
              }
              
              if (onChunk) {
                onChunk(chunk, cleanedContent);
              }
            });
            
            if (processedData.processedLength > 0) {
              lastProcessedIndex += processedData.processedLength;
            }
          } catch (error) {
            console.warn('Error processing translation streaming data:', error);
          } finally {
            isProcessingChunk = false;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Process any remaining data
    let finalParsedContent = '';
    console.log('Processing final translation data. Total accumulated length:', accumulatedData.length);
    console.log('Last processed index:', lastProcessedIndex);
    
    try {
      const remainingData = accumulatedData.substring(lastProcessedIndex);
      console.log('Remaining translation data to process:', remainingData.substring(0, 300) + '...');
      
      if (remainingData.trim()) {
        const processedData = processStreamingData(remainingData, (chunk, htmlContent) => {
          console.log('Final translation processing callback triggered with HTML content:', htmlContent ? htmlContent.substring(0, 100) + '...' : 'no content');
          if (onChunk) {
            onChunk(chunk, htmlContent);
          }
        });
        if (processedData.htmlContent) {
          finalParsedContent = processedData.htmlContent;
          console.log('Extracted final translation HTML content:', finalParsedContent.substring(0, 200) + '...');
        }
      }
      
      // If we still don't have content, try to extract from the entire accumulated data
      if (!finalParsedContent) {
        console.log('No final translation content yet, trying to extract from entire accumulated data...');
        finalParsedContent = extractFinalContent(accumulatedData);
        console.log('Extracted final translation content:', finalParsedContent ? finalParsedContent.substring(0, 200) + '...' : 'no content');
      }
    } catch (error) {
      console.warn('Error processing final translation data:', error);
      // Fallback to accumulated content
      finalParsedContent = accumulatedData;
      console.log('Using fallback accumulated translation content');
    }

    // Process the final content to fix any JSON issues
    let processedTranslatedContent = finalParsedContent;
    
    // Clean the final content
    if (typeof finalParsedContent === 'string') {
      // Remove JSON artifacts
      if (finalParsedContent.includes('"content":')) {
        const contentMatch = finalParsedContent.match(/"content":\s*"([\s\S]*?)"\s*(,|\})/);
        if (contentMatch) {
          processedTranslatedContent = contentMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
        }
      }
      
      // Remove metafields JSON artifacts
      processedTranslatedContent = processedTranslatedContent
        .replace(/",\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact
        .replace(/,\s*"metafields":\s*\}\s*$/, '') // Remove metafields JSON artifact (without quotes)
        .replace(/",\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere
        .replace(/,\s*"metafields":\s*\}\s*/, '') // Remove metafields JSON artifact anywhere (without quotes)
        .replace(/\s+$/, '') // Remove trailing whitespace
        .trim();
      
      // Convert plain text to HTML if needed
      if (processedTranslatedContent && !/<[^>]+>/.test(processedTranslatedContent)) {
        processedTranslatedContent = processedTranslatedContent.split('\n\n').map(paragraph => 
          paragraph.trim() ? `<p>${paragraph.trim()}</p>` : ''
        ).join('');
      }
    }
    
    // If the content looks like JSON, try to parse and fix it
    if (typeof processedTranslatedContent === 'string' && processedTranslatedContent.trim().startsWith('{')) {
      console.log('Content appears to be JSON, attempting to parse and fix...');
      const parsedJson = fixTranslationJsonContent(processedTranslatedContent);
      
      if (parsedJson) {
        console.log('Successfully parsed and fixed JSON content');
        processedTranslatedContent = parsedJson;
      } else {
        console.warn('Failed to parse JSON content, using raw string');
      }
    }

    // Call onComplete with the final result
    const finalResult = {
      translatedContent: processedTranslatedContent,
      translatedMetafields: formData.metafields || [], // Include translated metafields
      sourceLanguage: formData.source_language || "English",
      targetLanguage: formData.target_language || "English",
      targetRegion: formData.target_region || "Global"
    };

    if (onComplete) {
      onComplete(finalResult);
    }

    return finalResult;

  } catch (error) {
    console.error('Error translating content with streaming:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

// Test function to verify JSON fixing with the provided malformed JSON
export const testTranslationJsonFixing = (jsonString) => {
  console.log('Testing JSON fixing with provided string...');
  console.log('Original string:', jsonString.substring(0, 200) + '...');
  
  const result = fixTranslationJsonContent(jsonString);
  
  if (result) {
    console.log('✅ JSON fixing successful!');
    console.log('Fixed result:', result);
    return result;
  } else {
    console.log('❌ JSON fixing failed');
    return null;
  }
};

// Simple test function for the specific case
export const testSpecificJsonString = () => {
  const testJson = `{ "content": "", "metafields": [ { "id":51065021366550, "namespace": "custom", "key": "vaidyas", "value": "[\\"gid://shopify/Metaobject/83282166038\\"]", "description":None, "owner_id":152641044758, "created_at": "2025-09-25T14:18:18+05:30", "updated_at": "2025-09-25T14:18:18+05:30", "owner_resource": "page", "type": "list.metaobject_reference", "admin_graphql_api_id": "gid://shopify/Metafield/51065021366550", "metaobjects": [ { "id": "gid://shopify/Metaobject/83282166038", "type": "book_appointment", "handle": "this-is-vaidaya", "fields": [ { "key": "booking_date", "value": "2025-11-15" }, { "key": "booking_place_image", "value": "gid://shopify/MediaImage/42182516736278" }, { "key": "booking_sub_heading", "value": "Das ist Vaidaya" }, { "key": "booking_heading", "value": "Das ist Vaidaya" }, { "key": "booking_location", "value": "Indien" }, { "key": "booking_time", "value": "30 Minuten" }, { "key": "booking_price", "value": "499" }, { "key": "booking_link_text", "value": "Mehr erfahren" }, { "key": "booking_term_condition", "value": "{\\"type\\":\\"root\\",\\"children\\":[{\\"type\\":\\"paragraph\\",\\"children\\":[{\\"type\\":\\"text\\",\\"value\\":\\"Hochkarätige Ayurveda-Experten (Vaidyas) bereisen regelmäßig unser Land.\\"}]},{\\"type\\":\\"paragraph\\",\\"children\\":[{\\"type\\":\\"text\\",\\"value\\":\\"Nutzen Sie diese wertvolle Gelegenheit und buchen Sie eine halbstündige, individuell auf Sie abgestimmte Beratung inklusive einer erstklassigen ayurvedischen Pulsdiagnose.\\"}]},{\\"type\\":\\"paragraph\\",\\"children\\":[{\\"type\\":\\"text\\",\\"value\\":\\"Lassen Sie sich von dem tiefgreifenden Wissen aus der Schatzkiste des Ayurveda und den persönlichen, transformierenden Ratschlägen unserer Ayurveda-Experten inspirieren und finden Sie Ihre innere Balance. Sie können während der Beratung alle Fragen stellen, die Ihnen wichtig sind.\\"}]}]}" } ] } ], "booking_url": "https://maharishiayurvedaindia.com/" } ]}`;
  
  console.log('Testing with the specific JSON string...');
  return testTranslationJsonFixing(testJson);
};

// Quick test function to verify None replacement
export const testNoneReplacement = () => {
  const testString = '"description":None';
  console.log('Testing None replacement...');
  console.log('Original:', testString);
  
  let fixed = testString
    .replace(/:\s*None\b/g, ': null')
    .replace(/"([^"]+)":\s*None/g, '"$1": null')
    .replace(/"([^"]+)":None/g, '"$1": null');
  
  console.log('Fixed:', fixed);
  return fixed;
};

// Mock generation function for development/testing (kept for fallback)
export const mockGenerateContent = async (formData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  let mockContent = "";
  
  if (formData.dataStructure === "array") {
    mockContent = [
      "First item in the array",
      "Second item in the array",
      "Third item in the array"
    ];
  } else if (formData.dataStructure === "object") {
    mockContent = {
      key1: "Value 1",
      key2: "Value 2",
      key3: "Value 3"
    };
  } else {
    mockContent = "This is a generated string response based on your prompt.";
  }
  
  return {
    id: Date.now(),
    name: formData.prompt.substring(0, 50) + "...",
    prompt: formData.prompt,
    created: new Date().toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }),
    language: formData.language,
    dataStructure: formData.dataStructure,
    renderingFormat: formData.renderingFormat,
    locationFocus: formData.locationFocus,
    generatedContent: JSON.stringify(mockContent, null, 2),
    status: "draft"
  };
};
